import MenuIcon from '@mui/icons-material/Menu';
import Alert from '@mui/material/Alert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { fetchSites, type Site } from '../fleetApi';
import { linkHandler, navigate, routePath, type Route } from '../router';
import { useSession } from '../sessionContext';
import { navSections } from './nav';

const DRAWER_WIDTH = 264;

interface AppShellProps {
  route: Route;
  environment: string;
  /** "web dev · api sha-…" for the footer. */
  versions?: string;
  children: React.ReactNode;
}

/**
 * Sidebar navigation with the tenant → site context switcher (Cockpit's host
 * switcher), a slim top bar, and the page. The sidebar is permanent from the
 * md breakpoint and a drawer below it.
 */
export function AppShell({ route, environment, versions, children }: AppShellProps) {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const session = useSession();
  const sites = useTenantSites();
  const siteId = 'siteId' in route ? route.siteId : undefined;
  const siteName = sites.find((s) => s.id === siteId)?.name;

  const close = () => {
    setOpen(false);
  };
  const sidebar = (
    <Sidebar
      route={route}
      sites={sites}
      siteId={siteId}
      siteName={siteName}
      versions={versions}
      onNavigate={close}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          width: { md: `calc(100% - ${String(DRAWER_WIDTH)}px)` },
          ml: { md: `${String(DRAWER_WIDTH)}px` },
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!wide && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              data-testid="nav-open"
              onClick={() => {
                setOpen(true);
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            component="a"
            href="/"
            onClick={linkHandler('/')}
            sx={{
              fontWeight: 700,
              fontSize: 18,
              color: 'inherit',
              textDecoration: 'none',
              display: { md: 'none' },
            }}
          >
            IllumiLink
          </Typography>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {session.membership && (
              <Typography
                noWrap
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: 'block' }, textAlign: { md: 'left' } }}
              >
                {session.membership.tenant_name}
                {siteName ? ` › ${siteName}` : ''}
              </Typography>
            )}
          </Box>
          {environment !== 'production' && (
            <Chip size="small" color="warning" label={environment} />
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        aria-label="Main"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {wide ? (
          <Drawer
            variant="permanent"
            open
            sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
          >
            {sidebar}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={open}
            onClose={close}
            // Mounted while closed so tests (and screen readers) can find the
            // signed-in user without opening it.
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${String(DRAWER_WIDTH)}px)` },
          px: { xs: 2, sm: 3 },
          pb: 4,
        }}
      >
        <Toolbar />
        <Box sx={{ maxWidth: 1200, mx: 'auto', pt: { xs: 2, sm: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
}

/** The selected tenant's sites, for the site switcher. */
function useTenantSites(): Site[] {
  const { apiUrl, token, membership } = useSession();
  const tenantId = membership?.tenant_id;
  const [sites, setSites] = useState<{ tenantId: string; sites: Site[] } | null>(null);
  useEffect(() => {
    if (!tenantId || !token) return;
    const controller = new AbortController();
    fetchSites(apiUrl, token, tenantId, controller.signal)
      .then((list) => {
        setSites({ tenantId, sites: list });
      })
      .catch(() => {
        // The pages report load errors; the switcher just stays empty.
      });
    return () => {
      controller.abort();
    };
  }, [apiUrl, tenantId, token]);
  return sites && tenantId && sites.tenantId === tenantId ? sites.sites : [];
}

interface SidebarProps {
  route: Route;
  sites: Site[];
  siteId?: string;
  siteName?: string;
  versions?: string;
  onNavigate: () => void;
}

function Sidebar({ route, sites, siteId, siteName, versions, onNavigate }: SidebarProps) {
  const session = useSession();
  const { me, membership } = session;
  const go = (path: string) => (e: React.MouseEvent) => {
    linkHandler(path)(e);
    onNavigate();
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography
          component="a"
          href="/"
          onClick={go('/')}
          sx={{ fontWeight: 700, fontSize: 18, color: 'inherit', textDecoration: 'none' }}
        >
          IllumiLink
        </Typography>
      </Toolbar>
      <Divider />

      <Stack spacing={1.5} sx={{ p: 2 }} data-testid="context-switcher">
        {me.kind === 'loading' || me.kind === 'idle' ? (
          <Typography variant="body2" color="text.secondary">
            Loading your tenants…
          </Typography>
        ) : me.kind === 'error' ? (
          <Alert severity="error">Could not load your account: {me.error}</Alert>
        ) : me.me.memberships.length === 0 ? (
          <Alert severity="info" data-testid="no-tenant">
            Your account isn&apos;t linked to a tenant yet. Ask your administrator for access.
          </Alert>
        ) : membership ? (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel id="tenant-label">Tenant</InputLabel>
              <Select
                labelId="tenant-label"
                label="Tenant"
                value={membership.tenant_id}
                data-testid="tenant-select"
                onChange={(e) => {
                  session.setTenant(e.target.value);
                  navigate('/');
                  onNavigate();
                }}
              >
                {me.me.memberships.map((m) => (
                  <MenuItem key={m.tenant_id} value={m.tenant_id}>
                    {m.tenant_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Chip
              size="small"
              label={membership.role}
              data-testid="tenant-role"
              sx={{ alignSelf: 'start' }}
            />
            {sites.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="site-label">Site</InputLabel>
                <Select
                  labelId="site-label"
                  label="Site"
                  value={siteId && sites.some((s) => s.id === siteId) ? siteId : ''}
                  displayEmpty={false}
                  data-testid="site-select"
                  onChange={(e) => {
                    navigate(routePath({ name: 'site', siteId: e.target.value }));
                    onNavigate();
                  }}
                >
                  {sites.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        ) : null}
      </Stack>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {membership &&
          navSections(route, session.permissions, siteName).map((section, i) => (
            <List
              key={section.title ?? String(i)}
              dense
              data-testid={section.testId}
              subheader={
                section.title ? (
                  <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                    {section.title}
                  </ListSubheader>
                ) : undefined
              }
            >
              {section.items.map((it) => {
                const path = routePath(it.route);
                const selected = it.match(route);
                return (
                  <ListItemButton
                    key={it.testId}
                    component="a"
                    href={path}
                    onClick={go(path)}
                    selected={selected}
                    aria-current={selected ? 'page' : undefined}
                    data-testid={it.testId}
                    sx={{ mx: 1, borderRadius: 1 }}
                  >
                    <ListItemText primary={it.label} />
                  </ListItemButton>
                );
              })}
            </List>
          ))}
      </Box>

      <Divider />
      <Stack spacing={1} sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }} data-testid="signed-in-as">
          Signed in as {session.email}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={session.signOut}
          data-testid="sign-out"
          sx={{ alignSelf: 'start' }}
        >
          Sign out
        </Button>
        {versions && (
          <Typography variant="caption" color="text.secondary" data-testid="versions">
            {versions}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
